//External libs
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

//Local libs
import Service from 'src/app/common/service';
import UOMConversionModel from './uomConversion.model';

/**
 * @service UomConversionService
 * @description Service for managing Unit of Measure (UOM) conversions within the Material Management System (MMS).
 * This service handles the definitions and logic required to convert quantities from one unit to another
 * (e.g., converting 'Bags' to 'Kilograms' or 'Liters' to 'Gallons').
 *
 * Accurate UOM conversion is critical for:
 * 1. **Inventory Management**: Normalizing stock levels when items are purchased and consumed in different units.
 * 2. **Costing**: Calculating unit costs correctly across different packaging sizes.
 * 3. **Reporting**: Aggregating usage data into standard units for analysis.
 *
 * The service interacts with the `/mms/uomconversion/` backend endpoint.
 *
 * @feature 1. Retrieve UOM conversion rates.
 * @feature 2. Manage conversion factors between specific UOM pairs.
 *
 * @architecture
 * Extends base Service<UOMConversionModel> providing REST API integration.
 * Uses HttpClient for HTTP operations.
 *
 * @service-endpoint /mms/uomconversion/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class UomConversionService extends Service<UOMConversionModel> {
  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/uomconversion/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/uomconversion/');
  }
}
